import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";
import {
    Alert,
    alpha,
    Box,
    Button,
    CircularProgress,
    Container,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { useDispatch, useSelector } from "react-redux";
import Popup from "../../components/Popup";
import StudentEnrollmentFormFields from "../../components/student/StudentEnrollmentFormFields";
import { buildStudentRegPayload, postStudentRegistration } from "../../components/student/buildStudentRegPayload";
import { getApiBaseUrl } from "../../config/api";
import { getSchoolIdForUser } from "../../utils/userContext";
import { isAtStudentLimit } from "../../config/subscriptionPlans";
import { useHomeroomTeacherTerm } from "../../hooks/useHomeroomTeacherTerm";
import { useStudentEnrollmentForm } from "../../hooks/useStudentEnrollmentForm";
import { getSecondaryFormLevel } from "../../utils/classScopeMatch";
import { FORM3_MIN_SUBJECTS, isSubjectChoiceFormLevel } from "../../utils/form3SubjectSelection";
import Form3SubjectPicker from "../../components/class/Form3SubjectPicker";
import { getSubjectList } from "../../redux/sclassRelated/sclassHandle";

const TERM_OPTIONS = ["Term 1", "Term 2", "Term 3"];

const TeacherEnrollStudent = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((s) => s.user);
    const { subjectsList, loading: subjectsLoading } = useSelector((s) => s.sclass);
    const teacherId = currentUser?._id || currentUser?.id;
    const schoolId = getSchoolIdForUser(currentUser);

    const [classes, setClasses] = useState([]);
    const [classId, setClassId] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [reconciliation, setReconciliation] = useState(null);

    const [declaredTotal, setDeclaredTotal] = useState("");
    const [term, setTerm] = useState("Term 1");
    const [academicYear, setAcademicYear] = useState(String(new Date().getFullYear()));
    const [declarationId, setDeclarationId] = useState(null);
    const [declSaving, setDeclSaving] = useState(false);

    const [form3SubjectIds, setForm3SubjectIds] = useState(() => new Set());
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [popupSeverity, setPopupSeverity] = useState(undefined);

    const atPlanLimit = isAtStudentLimit(currentUser);
    const homeroomTerm = useHomeroomTeacherTerm();
    const activeClass = useMemo(
        () => classes.find((c) => (c._id || c.id) === classId) || null,
        [classes, classId],
    );
    const subjectChoiceFormLevel = getSecondaryFormLevel(activeClass);
    const isSubjectChoiceClass = isSubjectChoiceFormLevel(subjectChoiceFormLevel);
    const form3Subjects = useMemo(
        () => (Array.isArray(subjectsList) ? subjectsList : []),
        [subjectsList],
    );

    useEffect(() => {
        if (!isSubjectChoiceClass || !classId) {
            setForm3SubjectIds(new Set());
            return;
        }
        dispatch(getSubjectList(classId, "ClassSubjects"));
        setForm3SubjectIds(new Set());
    }, [dispatch, isSubjectChoiceClass, classId]);

    const toggleForm3Subject = (subjectId) => {
        setForm3SubjectIds((prev) => {
            const next = new Set(prev);
            if (next.has(subjectId)) next.delete(subjectId);
            else next.add(subjectId);
            return next;
        });
    };

    const {
        values,
        setField,
        photoFile,
        resetForm,
        validateForSubmit,
        formContext,
        showGuardianSection,
        showResidenceSection,
    } = useStudentEnrollmentForm({
        currentUser,
        selectedClass: activeClass,
        initialClassId: classId,
    });

    useEffect(() => {
        if (classId && values.sclassName !== classId) {
            setField("sclassName", classId);
        }
    }, [classId, setField, values.sclassName]);

    const loadClasses = useCallback(async () => {
        if (!teacherId) return;
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${getApiBaseUrl()}/Enrollment/teacher/${teacherId}/classes`,
            );
            const list = Array.isArray(data) ? data : [];
            setClasses(list);
            if (list.length === 1) setClassId(list[0]._id || list[0].id);
        } catch {
            setClasses([]);
        } finally {
            setLoading(false);
        }
    }, [teacherId]);

    const loadReconciliation = useCallback(async () => {
        if (!classId) {
            setReconciliation(null);
            return;
        }
        try {
            const { data } = await axios.get(
                `${getApiBaseUrl()}/Enrollment/reconciliation/${classId}?academicYear=${encodeURIComponent(academicYear)}&term=${encodeURIComponent(term)}`,
            );
            setReconciliation(data);
            if (data?.declaration?.id || data?.declaration?._id) {
                setDeclarationId(data.declaration.id || data.declaration._id);
                setDeclaredTotal(String(data.declaration.declaredTotal ?? ""));
            }
        } catch {
            setReconciliation(null);
        }
    }, [classId, academicYear, term]);

    useEffect(() => {
        loadClasses();
    }, [loadClasses]);

    useEffect(() => {
        loadReconciliation();
    }, [loadReconciliation]);

    const handleEnroll = async (e) => {
        e.preventDefault();
        if (!classId || atPlanLimit) return;

        const validationError = validateForSubmit();
        if (validationError) {
            setMessage(validationError);
            setPopupSeverity("warning");
            setShowPopup(true);
            return;
        }
        if (isSubjectChoiceClass) {
            if (form3Subjects.length < FORM3_MIN_SUBJECTS) {
                setMessage(
                    `This ${subjectChoiceFormLevel} class needs at least ${FORM3_MIN_SUBJECTS} subjects before you can enroll a pupil.`,
                );
                setPopupSeverity("warning");
                setShowPopup(true);
                return;
            }
            if (form3SubjectIds.size < FORM3_MIN_SUBJECTS) {
                setMessage(`Select at least ${FORM3_MIN_SUBJECTS} subjects for this ${subjectChoiceFormLevel} pupil.`);
                setPopupSeverity("warning");
                setShowPopup(true);
                return;
            }
        }

        setSaving(true);
        try {
            const payload = buildStudentRegPayload(
                { ...values, sclassName: classId },
                {
                    adminID: schoolId,
                    selectedClass: activeClass,
                    showResidenceSection,
                    showGuardianSection,
                    isUniversity: activeClass?.educationLevel === "University",
                    enrollmentSource: "formTeacher",
                    submittedByTeacherId: teacherId,
                    photoFile,
                    subjectIds: isSubjectChoiceClass ? [...form3SubjectIds] : undefined,
                },
            );

            const { data } = await postStudentRegistration(payload, photoFile);
            if (data?.message && !data?.id && !data?._id) {
                setMessage(data.message);
                setPopupSeverity("warning");
            } else {
                setMessage(data.enrollmentNotice || "Student submitted for director verification.");
                setPopupSeverity("success");
                resetForm(true);
                loadReconciliation();
            }
            setShowPopup(true);
        } catch (err) {
            setMessage(
                err.response?.data?.message ||
                    err.response?.data?.error ||
                    (err.response?.status === 403
                        ? "Enrollment was blocked (access denied). Sign out and sign in again as the form teacher, or ask your director to verify your class assignment."
                        : "Could not submit enrollment"),
            );
            setPopupSeverity("error");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    const handleSaveDeclaration = async () => {
        if (!classId) return;
        setDeclSaving(true);
        try {
            const { data } = await axios.post(`${getApiBaseUrl()}/Enrollment/declaration`, {
                teacherId,
                schoolId,
                classId,
                academicYear,
                term,
                declaredTotal: Number(declaredTotal),
                declarationId,
            });
            if (data?.message && !data?.id && !data?._id) {
                setMessage(data.message);
                setPopupSeverity("warning");
            } else {
                setDeclarationId(data.id || data._id);
                setMessage("Class roll count saved as draft.");
                setPopupSeverity("success");
                loadReconciliation();
            }
            setShowPopup(true);
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not save declaration");
            setPopupSeverity("error");
            setShowPopup(true);
        } finally {
            setDeclSaving(false);
        }
    };

    const handleSubmitDeclaration = async () => {
        if (!declarationId) {
            setMessage("Save the class roll count first.");
            setPopupSeverity("warning");
            setShowPopup(true);
            return;
        }
        setDeclSaving(true);
        try {
            const { data } = await axios.put(
                `${getApiBaseUrl()}/Enrollment/declaration/${declarationId}/submit`,
            );
            setMessage(data.message || "Declaration submitted to director.");
            setPopupSeverity(data.reconciliation?.flagged ? "warning" : "success");
            setShowPopup(true);
            loadReconciliation();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not submit declaration");
            setPopupSeverity("error");
            setShowPopup(true);
        } finally {
            setDeclSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!classes.length) {
        return (
            <Container maxWidth="md" sx={{ py: 3 }}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                    You have the <strong>{homeroomTerm.roleTitleCaps}</strong> role but no class is linked yet. Ask your
                    director to set you as <strong>{homeroomTerm.assignOnClass}</strong> under{" "}
                    <strong>All classes</strong>, then you can enroll students here for director approval.
                </Alert>
            </Container>
        );
    }

    const rec = reconciliation?.reconciliation;
    const plan = reconciliation?.plan;

    return (
        <Box
            sx={{
                minHeight: "100%",
                pb: 4,
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[50]} 22%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 } }}>
                <Button component={RouterLink} to="/Teacher/my-class" size="small" sx={{ mb: 1 }}>
                    ← Back to My class
                </Button>

                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                        mb: 2,
                    }}
                >
                    <Box
                        sx={{
                            px: { xs: 2, sm: 3 },
                            py: 2.5,
                            background: (t) =>
                                `linear-gradient(115deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 55%, ${alpha(t.palette.primary.light, 0.9)} 100%)`,
                            color: "common.white",
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <PersonAddAlt1Icon sx={{ fontSize: 32 }} />
                            <Box>
                                <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1 }}>
                                    {homeroomTerm.theRole}
                                </Typography>
                                <Typography variant="h5" fontWeight={800}>
                                    Enroll student
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.25, maxWidth: 520 }}>
                                    Same complete profile as director enrollment — pending until the school office
                                    verifies. No fees until approval.
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    {atPlanLimit ? (
                        <Alert severity="warning" sx={{ mx: { xs: 2, sm: 3 }, mt: 2, borderRadius: 2 }}>
                            Your school has reached the student limit for its subscription plan. The director must
                            upgrade before more enrollments can be added.
                        </Alert>
                    ) : null}

                    {plan?.maxStudents != null ? (
                        <Alert severity="info" variant="outlined" sx={{ mx: { xs: 2, sm: 3 }, mt: 2, borderRadius: 2 }}>
                            Plan slots: {plan.schoolSlotCount} / {plan.maxStudents} used
                            {plan.slotsRemaining != null ? ` (${plan.slotsRemaining} remaining)` : ""}. Pending
                            enrollments count toward the limit.
                        </Alert>
                    ) : null}

                    <Box sx={{ p: { xs: 2, sm: 3 } }}>
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel>Your class ({homeroomTerm.theRole})</InputLabel>
                            <Select
                                label={`Your class (${homeroomTerm.theRole})`}
                                value={classId}
                                onChange={(e) => setClassId(e.target.value)}
                            >
                                {classes.map((c) => {
                                    const id = c._id || c.id;
                                    return (
                                        <MenuItem key={id} value={id}>
                                            {c.sclassName} ({c._count?.students ?? 0} on roll)
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>

                        {reconciliation ? (
                            <Alert
                                severity={reconciliation.fraudRisk ? "warning" : "info"}
                                sx={{ borderRadius: 2, mb: 2 }}
                            >
                                On system: <strong>{reconciliation.verifiedCount}</strong> verified,{" "}
                                <strong>{reconciliation.pendingCount}</strong> pending
                                {rec?.declared != null ? (
                                    <>
                                        . Declared class size: <strong>{rec.declared}</strong>
                                        {rec.flagged ? (
                                            <>
                                                {" "}
                                                — <strong>{rec.gap} pupil(s)</strong> declared but not on the system.
                                            </>
                                        ) : null}
                                    </>
                                ) : (
                                    ". Submit your total class size below so the director can reconcile."
                                )}
                            </Alert>
                        ) : null}

                        <Paper
                            component="form"
                            variant="outlined"
                            onSubmit={handleEnroll}
                            sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}
                        >
                            {isSubjectChoiceClass && classId ? (
                                <Form3SubjectPicker
                                    formLevel={subjectChoiceFormLevel}
                                    subjects={form3Subjects}
                                    selectedIds={[...form3SubjectIds]}
                                    onToggle={toggleForm3Subject}
                                    loading={subjectsLoading}
                                />
                            ) : null}
                            <StudentEnrollmentFormFields
                                formContext={formContext}
                                variant="formTeacher"
                                situation="Class"
                                homeroomTerm={homeroomTerm}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                sx={{ mt: 3, borderRadius: 2, fontWeight: 700 }}
                                disabled={saving || atPlanLimit || !classId}
                            >
                                {saving ? "Submitting…" : "Submit for director verification"}
                            </Button>
                        </Paper>
                    </Box>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                        Class roll declaration (anti–fee theft)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Declare the <strong>total number of learners</strong> in{" "}
                        {activeClass?.sclassName || "your class"} (including those not yet on the system). The director
                        will compare this to verified + pending enrollments before fee collection.
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Term"
                                select
                                SelectProps={{ native: true }}
                                value={term}
                                onChange={(e) => setTerm(e.target.value)}
                            >
                                {TERM_OPTIONS.map((t) => (
                                    <option key={t} value={t}>
                                        {t}
                                    </option>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Academic year"
                                value={academicYear}
                                onChange={(e) => setAcademicYear(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Total learners in class"
                                value={declaredTotal}
                                onChange={(e) => setDeclaredTotal(e.target.value)}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Button
                                    variant="outlined"
                                    disabled={declSaving || !classId}
                                    onClick={handleSaveDeclaration}
                                >
                                    Save draft
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<SendOutlinedIcon />}
                                    disabled={declSaving || !declarationId}
                                    onClick={handleSubmitDeclaration}
                                >
                                    Submit to director
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>

                <Popup
                    message={message}
                    showPopup={showPopup}
                    setShowPopup={setShowPopup}
                    severity={popupSeverity}
                />
            </Container>
        </Box>
    );
};

export default TeacherEnrollStudent;
